import { useQuery } from '@tanstack/react-query';
import React from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from 'react-router-dom';

import { hooks } from '@cowbell/ui';
import { fetchQuoteDetailsOffProduct } from '../../../inbox/QuotesService';
import { Stepper, Steps } from './Stepper';

import { fetchPolicyEndorsementDetails } from '../../../policies/PolicyService';
import { EndorsementSummary } from '../MidtermEndorsement/Attest/EndorsementSummary';
import PaymentSummary from '../MidtermEndorsement/PaymentSummary';
import useNotifications from '../../../_lib/notifications/useNotifications';

export const MidTermEndorsementStepper = () => {
  const { step, goForward, jumpTo, goBack, isCompleted, completeStep } =
    hooks.useSteps({
      startStep: 'endorsement-paymentsummary',
      initialSteps: [
        'endorsement-paymentsummary',
        'endorsement-attest',
        'endorsement-epay',
      ],
    });
  const history = useHistory();
  // @ts-expect-error TS(2339): Property 'quoteId' does not exist on type '{}'.
  const { quoteId, policyNum, product, step: urlStep } = useParams();
  const notifications = useNotifications();
  const initial = React.useRef(true);

  const pathPrefix = `/customer/quotes/${quoteId}`;
  const pathSuffix = `${product}/policies/${policyNum}`;
  const ROOT_PATH_PREFIX = '/customer/quotes/:quoteId';
  const ROOT_PATH_SUFFIX = ':product/policies/:policyNum';
  const DEFAULT_REDIRECT = '/customer/policies';

  React.useEffect(() => {
    if (initial.current) {
      initial.current = false;
    } else {
      history.push(`${pathPrefix}/${step}/${pathSuffix}`);
    }
    // eslint-disable-next-line
  }, [step]);

  React.useEffect(() => {
    if (step !== urlStep) {
      jumpTo(urlStep);
    }
    // eslint-disable-next-line
  }, []);

  const {
    data: quote,
    isLoading: isQuoteLoading,
    isError: isQuoteError,
  } = useQuery(
    ['quote-data', quoteId],
    () => {
      return fetchQuoteDetailsOffProduct(product, quoteId)
        .then((resp) => {
          return resp.data;
        })
        .catch(() => {
          notifications.error(
            'There was an error while fetching quote information.'
          );
        });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: policy,
    isLoading: isPolicyLoading,
    isError: isPolicyError,
  } = useQuery(
    ['policy-data', policyNum],
    () => {
      return fetchPolicyEndorsementDetails(policyNum)
        .then((resp) => {
          return resp.data;
        })
        .catch(() => {
          notifications.error(
            'There was an error while fetching policy information.'
          );
        });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleStepSubmission = () => {
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    completeStep();
    goForward();
  };

  if (isQuoteLoading || isPolicyLoading) {
    return null;
  }

  if (isQuoteError || isPolicyError) {
    notifications.error('There was an error getting quote/policy data.');
    history.push(DEFAULT_REDIRECT);
  }

  return (
    <>
      <Stepper>
        <Steps
          id="endorsement-paymentsummary"
          step={step}
          isCompleted={isCompleted}
          jumpTo={jumpTo}
        >
          Payment Summary
        </Steps>
        <Steps
          id="endorsement-attest"
          step={step}
          isCompleted={isCompleted}
          jumpTo={jumpTo}
        >
          Attest
        </Steps>
        <Steps
          id="endorsement-epay"
          step={step}
          isCompleted={isCompleted}
          jumpTo={jumpTo}
        >
          e-Pay
        </Steps>
      </Stepper>
      <Switch>
        <Route
          path={`${ROOT_PATH_PREFIX}/endorsement-paymentsummary/${ROOT_PATH_SUFFIX}`}
          exact
          render={(props) => {
            return (
              <PaymentSummary
                onSubmit={handleStepSubmission}
                quote={quote}
                {...props}
              />
            );
          }}
        />
        <Route
          path={`${ROOT_PATH_PREFIX}/endorsement-attest/${ROOT_PATH_SUFFIX}`}
          exact
          render={(props) => {
            return (
              <EndorsementSummary
                onSubmit={handleStepSubmission}
                goBack={goBack}
                quote={quote}
                policy={policy}
                {...props}
              />
            );
          }}
        />
        <Redirect to={DEFAULT_REDIRECT} />
      </Switch>
    </>
  );
};
